<template>
  <custom-popup
    v-model="dialog"
    :cons="{ title: $t('qrUsersList.addUser') }"
    hide-confirm
    max-width="600"
    @close="resetFields"
  >
    <v-card class="pa-4" flat>
      <v-row no-gutters>
        <v-col class="pa-2" cols="12" md="6">
          <div class="rowinfo">
            <v-text-field
              v-model="userData.name"
              :rules="nameRules"
              :label="$t('qrUsersList.usersColumns.name')"
              outlined
              dense
              class="contentinfo"
              clearable
            />
            <help-info
              :element-name="`${serviceName}-${componentName}-name`"
              :service-name="serviceName"
              :help-data="helpData"
              class="buttoninfo"
              :label="$t('qrUsersList.usersColumns.name')"
            />
          </div>
        </v-col>

        <v-col class="pa-2" cols="12" md="6">
          <div class="rowinfo">
            <v-text-field
              v-model="userData.family"
              :rules="familyRules"
              :label="$t('qrUsersList.usersColumns.family')"
              outlined
              dense
              class="contentinfo"
              clearable
            />
            <help-info
              :element-name="`${serviceName}-${componentName}-family`"
              :service-name="serviceName"
              :help-data="helpData"
              class="buttoninfo"
              :label="$t('qrUsersList.usersColumns.family')"
            />
          </div>
        </v-col>

        <v-col class="pa-2" md="6" cols="12" style="direction: ltr !important">
          <div class="rowinfo">
            <div class="stylephone">
              <vue-tel-input
                v-model="userPhone"
                :default-country="defaultCountry"
                :preferred-countries="['IR', 'IQ', 'US', 'CA', 'GB', 'AE']"
                :placeholder="$t('qrUsersList.usersColumns.mobile')"
                class="rounded-lg phone-input contentinfo w-100"
                :class="{ 'error--text': !phoneValidated.valid && userPhone }"
                @input="onPhoneInput"
                @validate="onPhoneValidate"
                @keydown.enter="handleEnterKey"
              />
              <div
                v-if="!phoneValidated.valid && userPhone"
                class="error--text text-caption mt-1"
              >
                {{ $t("qrUsersList.invalidPhone") }}
              </div>
            </div>
            <help-info
              :element-name="`${serviceName}-${componentName}-mobile`"
              :service-name="serviceName"
              :help-data="helpData"
              class="buttoninfo"
              :label="$t('qrUsersList.usersColumns.mobile')"
            />
          </div>
        </v-col>
      </v-row>

      <v-card-actions class="justify-end mt-4">
        <v-spacer />
        <v-btn
          :loading="loading"
          :disabled="!isFormValid"
          class="btn-get-code rounded-lg"
          outlined
          @click="submitUser"
        >
          <v-icon left>mdi-check</v-icon>
          {{ $t("submit") }}
        </v-btn>
        <v-btn
          class="btn-delete rounded-lg"
          @click="resetFields"
          :disabled="!isFormValid"
        >
          <v-icon left>mdi-close</v-icon>
          {{ $t("cancel") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </custom-popup>
</template>

<script>
import { mapState } from "vuex";
import HelpInfo from "@/view/pages/qrland/components/helpAcordion/HelpInfo.vue";
import HelpInfoWrapper from "@/view/pages/qrland/components/helpAcordion/HelpInfoWrapper.vue";

export default {
  name: "addUser",
  components: { HelpInfo, HelpInfoWrapper },

  props: {
    value: Boolean,
    infoId: {
      type: [Number,String],
      default: 0,
    },
  },

  data() {
    return {
      helpData: [],
      serviceName: "Qrland",
      componentName: "addUser",
      loading: false,
      userData: {
        name: "",
        family: "",
      },
      userPhone: "",
      phoneValidated: {
        valid: false,
        number: "",
        country: null,
      },
      defaultCountry: "IR",
      nameRules: [
        (v) => !!v || this.$t("qrUsersList.validation.nameRequired"),
        (v) =>
          (v && v.length >= 2) ||
          this.$t("qrUsersList.validation.nameMinLength"),
      ],
      familyRules: [
        (v) => !!v || this.$t("qrUsersList.validation.familyRequired"),
        (v) =>
          (v && v.length >= 2) ||
          this.$t("qrUsersList.validation.familyMinLength"),
      ],
    };
  },

  computed: {
    ...mapState("local", ["qr_id"]),

    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    qrId() {
      return this.qr_id || this.infoId;
    },
    isFormValid() {
      return (
        this.userData.name &&
        this.userData.name.length >= 2 &&
        this.userData.family &&
        this.userData.family.length >= 2 &&
        this.phoneValidated.valid &&
        this.cleanPhone(this.phoneValidated.number)
      );
    },
  },

  methods: {
    cleanPhone(phone) {
      if (!phone) return "";
      let cleaned = phone;
      const persianDigits = [
        /۰/gi,
        /۱/gi,
        /۲/gi,
        /۳/gi,
        /۴/gi,
        /۵/gi,
        /۶/gi,
        /۷/gi,
        /۸/gi,
        /۹/gi,
      ];
      const arabicDigits = [
        /٠/g,
        /١/g,
        /٢/g,
        /٣/g,
        /٤/g,
        /٥/g,
        /٦/g,
        /٧/g,
        /٨/g,
        /٩/g,
      ];

      for (let i = 0; i < 10; i++) {
        cleaned = cleaned
          .replace(persianDigits[i], i)
          .replace(arabicDigits[i], i);
      }

      if (
        cleaned.startsWith("+98") ||
        cleaned.startsWith("98") ||
        cleaned.startsWith("09")
      ) {
        cleaned = cleaned.replace(/^\+?0?/, "98").replace(/\s+/g, "");
      } else {
        cleaned = cleaned.replace(/[^\d+]/g, "").replace(/\s+/g, "");
      }

      return cleaned.replace("+", "");
    },

    onPhoneInput(number) {
      this.userPhone = number || "";
    },

    onPhoneValidate({ number, valid, country }) {
      if (
        number !== undefined &&
        valid !== undefined &&
        country !== undefined
      ) {
        this.phoneValidated = { number, valid, country };
      }
    },

    handleEnterKey() {
      if (this.isFormValid) {
        this.submitUser();
      }
    },

    async submitUser() {
      if (!this.isFormValid || !this.qrId) {
        this.$toast.error(this.$t("qrUsersList.messages.fillAllFields"));
        return;
      }

      this.loading = true;
      const cleanedMobile = this.cleanPhone(this.phoneValidated.number);

      const payload = {
        name: this.userData.name.trim(),
        family: this.userData.family.trim(),
        mobile: cleanedMobile,
        qr_id: this.qrId,
      };

      try {
        await this.$api.post("/v1/testQr/user/create", payload);
        this.$toast.success(this.$t("qrUsersList.messages.userCreated"));
        this.$emit("user-created");
        this.resetFields();
        this.dialog = false;
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          this.$t("qrUsersList.messages.createError");
        this.$toast.error(message);
      } finally {
        this.loading = false;
      }
    },

    resetFields() {
      this.userData = {
        name: "",
        family: "",
      };
      this.userPhone = "";
      this.phoneValidated = {
        valid: false,
        number: "",
        country: null,
      };
    },
  },
};
</script>

<style scoped>
.phone-input {
  width: 100%;
}
.stylephone {
  display: flex;
  flex-direction: column;
  width: 100% !important;
}
</style>
